import React from "react";
import {
  Inbox,
  BookOpen,
  Wrench,
  Users,
  BarChart3,
  ShieldCheck,
  GitBranch,
  Sparkles,
} from "lucide-react";

const features = [
  {
    icon: Inbox,
    title: "Unified omnichannel inbox",
    body:
      "WhatsApp, email, voice, webchat, Instagram, Slack — every conversation, one thread, one customer record.",
    span: "md:col-span-2",
    accent: "#7C5CFF",
  },
  {
    icon: BookOpen,
    title: "Grounded in your knowledge",
    body: "Drop in docs, Notion, Helpcenter or URLs — agents answer only from verified, citation-ready sources.",
    accent: "#3DDFB3",
  },
  {
    icon: Wrench,
    title: "Tools & API connectors",
    body: "Let agents refund orders, update CRM fields, or trigger workflows through secure OpenAPI tools.",
    accent: "#F97316",
  },
  {
    icon: GitBranch,
    title: "Visual flow builder",
    body: "Design multi-agent routing, conditions and escalation — no code, deploy versioned in seconds.",
    span: "md:col-span-2",
    accent: "#7C5CFF",
  },
  {
    icon: Users,
    title: "Graceful human takeover",
    body: "Agents know when to escalate. Humans pick up mid-thread with full context and a suggested reply.",
    accent: "#3DDFB3",
  },
  {
    icon: BarChart3,
    title: "Analytics that matter",
    body: "Resolution rate, containment, CSAT per agent, token spend per conversation — on a single dashboard.",
    accent: "#F97316",
  },
  {
    icon: ShieldCheck,
    title: "Enterprise-grade by default",
    body: "SOC 2, GDPR, SSO, RBAC, per-workspace encryption and EU data residency.",
    span: "md:col-span-2",
    accent: "#7C5CFF",
  },
];

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-28 md:py-40"
    >
      <div className="pointer-events-none absolute top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] glow-purple opacity-40" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#7C5CFF]">
              ── Platform
            </span>
          </div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-white">
            Everything a{" "}
            <span className="italic text-white/90">human agent</span> has.
            <br />
            <span className="text-white/60">At software scale.</span>
          </h2>
          <p className="mt-6 text-white/60 text-lg max-w-2xl">
            One platform to ingest your knowledge, compose multi-step agents,
            route across every channel and measure outcomes — not vanity
            metrics.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div
              key={i}
              data-testid={`feature-card-${i}`}
              className={`card-surface p-7 md:p-8 relative overflow-hidden ${f.span || ""}`}
            >
              <div
                className="absolute -top-14 -right-14 w-44 h-44 rounded-full opacity-[0.08] blur-2xl"
                style={{ background: f.accent }}
              />
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: `${f.accent}14`,
                  border: `1px solid ${f.accent}40`,
                }}
              >
                <f.icon className="w-5 h-5" style={{ color: f.accent }} />
              </div>
              <h3 className="font-serif text-[26px] md:text-[28px] leading-[1.15] text-white mb-3">
                {f.title}
              </h3>
              <p className="text-white/55 text-[15px] leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}

          {/* Final cell: callout */}
          <div className="grad-border p-7 md:p-8 relative overflow-hidden">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[#3DDFB3] mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Mission control
            </div>
            <h3 className="font-serif text-[26px] md:text-[28px] leading-[1.15] text-white mb-3">
              Replay, debug, ship.
            </h3>
            <p className="text-white/55 text-[15px] leading-relaxed">
              Every run is traced. Inspect the exact KB chunks, tool calls, and
              tokens behind any message — then A/B a better prompt.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
